<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import BankSettingsAPI, { BankApiKeysAPI } from 'dashboard/api/bankSettings';

const loading = ref(false);
const saving = ref(false);
const banks = ref([]);
const keys = ref([]);
const newKey = ref('');

const showModal = ref(false);
const isEdit = ref(false);
const hasPassword = ref(false);
const hasApiToken = ref(false);
const form = ref({});

const origin = window.location.origin;

const authExample = [
  '{',
  '  "method": "POST",',
  '  "url": "https://auth.banco.com/token",',
  '  "headers": { "Content-Type": "application/x-www-form-urlencoded" },',
  '  "body_format": "form",',
  '  "body": { "grant_type": "password", "username": "{{username}}", "password": "{{password}}" },',
  '  "token_path": "access_token",',
  '  "expires_in_path": "expires_in"',
  '}',
].join('\n');

// v43: modelo de login explicito — 'basic' (so o token, ex. UY3/FACTA) ou
// 'senha' (usuario+senha, ex. C6/SOMA/V8). No modo basic o JSON e montado
// sozinho; o avancado continua existindo para casos fora do padrao.
const emptyForm = () => ({
  id: null,
  bank_name: '',
  auth_url: '',
  login_tipo: 'basic',
  scope: '',
  username: '',
  password: '',
  api_token: '',
  authText: '',
  refresh_interval_minutes: 0,
  codigo_origem_6: '',
  numero_cpf_certificado: '',
  desativado: false,
  use_proxy: false,
  proxy_url: '',
});

const fetchBanks = async () => {
  loading.value = true;
  try {
    const { data } = await BankSettingsAPI.get();
    banks.value = data.data || [];
  } catch (e) {
    useAlert('Erro ao carregar os bancos');
  } finally {
    loading.value = false;
  }
};

const fetchKeys = async () => {
  try {
    const { data } = await BankApiKeysAPI.get();
    keys.value = data.data || [];
  } catch (e) {
    keys.value = [];
  }
};

const tokenStatus = bank => {
  // Banco desativado: não gera token e não aparece no simulador.
  if (bank.enabled === false)
    return { label: 'DESATIVADO', cls: 'text-slate-400 font-semibold uppercase' };
  if (!bank.has_token) return { label: 'Sem token', cls: 'text-slate-400' };
  const exp = bank.token_expires_at ? new Date(bank.token_expires_at) : null;
  if (exp && exp < new Date())
    return { label: 'Expirado', cls: 'text-amber-600' };
  return { label: 'Ativo', cls: 'text-green-600' };
};

const openAdd = () => {
  isEdit.value = false;
  hasPassword.value = false;
  form.value = emptyForm();
  showModal.value = true;
};

const openEdit = async bank => {
  isEdit.value = true;
  showModal.value = true;
  form.value = emptyForm();
  try {
    const { data } = await BankSettingsAPI.show(bank.id);
    hasPassword.value = data.has_password;
    hasApiToken.value = data.has_api_token;
    const cfg = data.auth_config || {};
    const cfgStr = JSON.stringify(cfg);
    // URL sai de dentro do JSON e vive no campo do topo (pedido do Kairo).
    const authUrl = data.auth_url || cfg.url || '';
    // Config "padrao Basic" (o que o modo simples gera) vira campos limpos —
    // sem JSON na tela. Qualquer coisa fora disso cai no Avancado.
    const ehBasicPadrao =
      cfg.headers?.Authorization === 'Basic {{token}}' &&
      (cfg.body_format || 'json') === 'form' &&
      cfg.body?.grant_type === 'client_credentials' &&
      Object.keys(cfg.body || {}).every(k => ['grant_type', 'scope'].includes(k));
    form.value = {
      id: data.id,
      bank_name: data.bank_name,
      auth_url: authUrl,
      login_tipo:
        data.has_api_token || cfgStr.includes('{{token}}') ? 'basic' : 'senha',
      scope: ehBasicPadrao ? cfg.body?.scope || '' : '',
      username: data.username || '',
      password: '',
      api_token: '',
      authText:
        !ehBasicPadrao && Object.keys(cfg).length
          ? JSON.stringify(
              Object.fromEntries(Object.entries(cfg).filter(([k]) => k !== 'url')),
              null,
              2
            )
          : '',
      refresh_interval_minutes: data.refresh_interval_minutes || 0,
      codigo_origem_6: data.codigo_origem_6 || '',
      numero_cpf_certificado: data.numero_cpf_certificado || '',
      desativado: data.enabled === false,
      use_proxy: data.use_proxy === true,
      proxy_url: data.proxy_url || '',
    };
  } catch (e) {
    useAlert('Erro ao abrir o banco');
    showModal.value = false;
  }
};

const closeModal = () => {
  showModal.value = false;
};

const save = async () => {
  if (!isEdit.value && !form.value.bank_name.trim()) {
    useAlert('Informe o nome do banco');
    return;
  }
  let authConfig = {};
  const t = (form.value.authText || '').trim();
  if (t) {
    try {
      authConfig = JSON.parse(t);
    } catch (e) {
      useAlert('O "JSON de Autenticação" não é um JSON válido');
      return;
    }
  } else if (form.value.login_tipo === 'basic') {
    // Modo simples: monta o OAuth2 client_credentials sozinho. A URL vem do
    // campo do topo (auth_url) — nunca de dentro do JSON.
    const body = { grant_type: 'client_credentials' };
    if (form.value.scope.trim()) body.scope = form.value.scope.trim();
    authConfig = {
      method: 'POST',
      headers: {
        Authorization: 'Basic {{token}}',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body_format: 'form',
      body,
      token_path: 'access_token',
      expires_in_path: 'expires_in',
    };
  }
  // Aceita o token colado com o prefixo ("Basic MjhhMGFi…"): o header ja poe
  // o "Basic ", entao o prefixo duplicaria e o banco negaria o login.
  const tokenLimpo = form.value.api_token.trim().replace(/^basic\s+/i, '');
  saving.value = true;
  const payload = {
    bank_setting: {
      bank_name: form.value.bank_name.trim(),
      auth_url: form.value.auth_url,
      username: form.value.login_tipo === 'basic' ? '' : form.value.username,
      password: form.value.login_tipo === 'basic' ? '' : form.value.password,
      api_token: tokenLimpo,
      auth_config: authConfig,
      refresh_interval_minutes: Number(form.value.refresh_interval_minutes) || 0,
      enabled: !form.value.desativado,
      codigo_origem_6: form.value.codigo_origem_6,
      numero_cpf_certificado: form.value.numero_cpf_certificado,
      use_proxy: form.value.use_proxy,
      proxy_url: form.value.use_proxy ? form.value.proxy_url : '',
    },
  };
  try {
    if (isEdit.value) await BankSettingsAPI.update(form.value.id, payload);
    else await BankSettingsAPI.create(payload);
    useAlert('Banco salvo com sucesso!');
    showModal.value = false;
    await fetchBanks();
  } catch (e) {
    useAlert(e?.response?.data?.errors?.[0] || 'Erro ao salvar o banco');
  } finally {
    saving.value = false;
  }
};

const generateNow = async bank => {
  useAlert(`Gerando token do ${bank.bank_name.toUpperCase()}…`);
  try {
    await BankSettingsAPI.refreshToken(bank.id);
    useAlert('Token gerado com sucesso!');
    await fetchBanks();
  } catch (e) {
    useAlert(e?.response?.data?.errors?.[0] || 'Falha ao gerar o token');
    await fetchBanks();
  }
};

const removeBank = async bank => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Remover o banco "${bank.bank_name}"?`)) return;
  try {
    await BankSettingsAPI.delete(bank.id);
    useAlert('Banco removido');
    await fetchBanks();
  } catch (e) {
    useAlert('Erro ao remover');
  }
};

const createKey = async () => {
  // eslint-disable-next-line no-alert
  const label = window.prompt('Nome do sistema que vai usar esta chave:', 'sistema');
  if (label === null) return;
  try {
    const { data } = await BankApiKeysAPI.create({ label });
    newKey.value = data.api_key;
    await fetchKeys();
  } catch (e) {
    useAlert('Erro ao gerar a chave');
  }
};

const revokeKey = async key => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Revogar a chave "${key.label}"? Os sistemas que a usam param de funcionar.`)) return;
  try {
    await BankApiKeysAPI.delete(key.id);
    await fetchKeys();
  } catch (e) {
    useAlert('Erro ao revogar');
  }
};

const copyKey = async () => {
  try {
    await navigator.clipboard.writeText(newKey.value);
    useAlert('Chave copiada!');
  } catch (e) {
    useAlert('Copie manualmente a chave acima.');
  }
};

const isEmpty = computed(() => !loading.value && banks.value.length === 0);

onMounted(() => {
  fetchBanks();
  fetchKeys();
});
</script>

<template>
  <div class="p-6 mx-auto max-w-4xl">
    <!-- Cabeçalho -->
    <div class="flex items-start justify-between mb-6">
      <div>
        <h1 class="text-2xl font-semibold text-slate-900 dark:text-slate-50">
          Credenciais dos Bancos
        </h1>
        <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Cadastre o banco com usuário/senha e o JSON de autenticação. O token é
          gerado automaticamente no intervalo definido e servido aos sistemas via
          chave de API — sem expor as credenciais.
        </p>
      </div>
      <button
        type="button"
        class="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700"
        @click="openAdd"
      >
        <span class="text-lg leading-none">+</span> Adicionar Banco
      </button>
    </div>

    <div v-if="loading" class="py-12 text-center text-slate-400">Carregando…</div>

    <div
      v-else-if="isEmpty"
      class="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 py-14 text-center"
    >
      <p class="text-slate-500 dark:text-slate-400">Nenhum banco cadastrado ainda.</p>
      <button type="button" class="mt-3 text-sm font-medium text-green-600 hover:underline" @click="openAdd">
        + Adicionar o primeiro banco
      </button>
    </div>

    <!-- Lista de bancos -->
    <div v-else class="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
      <table class="w-full text-sm">
        <thead class="bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
          <tr>
            <th class="px-4 py-3 text-left font-medium">Banco</th>
            <th class="px-4 py-3 text-left font-medium">Usuário</th>
            <th class="px-4 py-3 text-left font-medium">Renova</th>
            <th class="px-4 py-3 text-left font-medium">Token</th>
            <th class="px-4 py-3 text-right font-medium">Ações</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
          <tr
            v-for="bank in banks"
            :key="bank.id"
            class="bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <td class="px-4 py-3 font-semibold uppercase text-slate-800 dark:text-slate-100">
              {{ bank.bank_name }}
            </td>
            <td class="px-4 py-3 text-slate-600 dark:text-slate-300">
              {{ bank.username || '—' }}
              <span v-if="bank.has_password" class="ml-1 text-xs text-slate-400" title="Senha salva">🔒</span>
            </td>
            <td class="px-4 py-3 text-slate-600 dark:text-slate-300">
              {{ bank.refresh_interval_minutes > 0 ? bank.refresh_interval_minutes + ' min' : '—' }}
            </td>
            <td class="px-4 py-3">
              <span :class="['font-medium', tokenStatus(bank).cls]">{{ tokenStatus(bank).label }}</span>
              <!-- Banco desativado: só "DESATIVADO", sem erro antigo de refresh. -->
              <div
                v-if="bank.enabled !== false && bank.last_refresh_status && bank.last_refresh_status !== 'ok'"
                class="text-xs text-red-500 max-w-[180px] truncate"
                :title="bank.last_refresh_status"
              >
                {{ bank.last_refresh_status }}
              </div>
            </td>
            <td class="px-4 py-3">
              <div class="flex justify-end gap-3 whitespace-nowrap">
                <button type="button" class="text-green-600 hover:underline" @click="generateNow(bank)">
                  Gerar Token
                </button>
                <button type="button" class="text-blue-600 hover:underline" @click="openEdit(bank)">
                  Editar
                </button>
                <button type="button" class="text-red-600 hover:underline" @click="removeBank(bank)">
                  Remover
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Chaves de API -->
    <div class="mt-8 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
      <div class="flex items-start justify-between">
        <div>
          <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-50">
            Chaves de acesso (API)
          </h2>
          <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Os sistemas usam a chave pra ler o token — sem ver usuário/senha:
          </p>
          <code class="mt-1 inline-block rounded bg-slate-100 dark:bg-slate-800 px-2 py-1 text-xs">
            GET {{ origin }}/bank-token/&lt;banco&gt; · header <b>X-Api-Key</b>
          </code>
        </div>
        <button
          type="button"
          class="rounded-lg border border-green-600 px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-50 dark:hover:bg-green-950"
          @click="createKey"
        >
          Gerar chave
        </button>
      </div>

      <!-- chave recém-criada (aparece só uma vez) -->
      <div
        v-if="newKey"
        class="mt-4 rounded-lg border border-green-300 bg-green-50 dark:bg-green-950/40 p-3"
      >
        <p class="text-xs text-green-700 dark:text-green-300 mb-1">
          Copie agora — esta chave não será exibida de novo:
        </p>
        <div class="flex items-center gap-2">
          <code class="flex-1 break-all text-sm">{{ newKey }}</code>
          <button type="button" class="text-sm text-green-700 hover:underline" @click="copyKey">Copiar</button>
          <button type="button" class="text-sm text-slate-400 hover:underline" @click="newKey = ''">Fechar</button>
        </div>
      </div>

      <table v-if="keys.length" class="w-full mt-4 text-sm">
        <thead class="text-slate-500 dark:text-slate-400">
          <tr>
            <th class="py-2 text-left font-medium">Sistema</th>
            <th class="py-2 text-left font-medium">Chave</th>
            <th class="py-2 text-left font-medium">Status</th>
            <th class="py-2 text-right font-medium">Ações</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
          <tr v-for="k in keys" :key="k.id">
            <td class="py-2 text-slate-700 dark:text-slate-200">{{ k.label }}</td>
            <td class="py-2 font-mono text-xs text-slate-500">{{ k.masked }}</td>
            <td class="py-2">
              <span :class="k.revoked ? 'text-red-500' : 'text-green-600'">
                {{ k.revoked ? 'Revogada' : 'Ativa' }}
              </span>
            </td>
            <td class="py-2 text-right">
              <button
                v-if="!k.revoked"
                type="button"
                class="text-red-600 hover:underline"
                @click="revokeKey(k)"
              >
                Revogar
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else class="mt-3 text-sm text-slate-400">Nenhuma chave gerada ainda.</p>
    </div>

    <!-- Modal adicionar/editar -->
    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto"
      @click.self="closeModal"
    >
      <div class="w-full max-w-lg rounded-xl bg-white dark:bg-slate-900 p-6 shadow-xl my-8">
        <h2 class="mb-4 text-xl font-semibold text-slate-900 dark:text-slate-50">
          {{ isEdit ? 'Editar banco' : 'Adicionar banco' }}
        </h2>

        <div class="space-y-4">
          <label class="block">
            <span class="text-sm font-medium text-slate-700 dark:text-slate-300">Nome do banco</span>
            <input
              v-model="form.bank_name"
              type="text"
              :disabled="isEdit"
              placeholder="ex.: uy3, facta, bmg…"
              class="mt-1 block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2 disabled:opacity-60"
            >
          </label>

          <label class="block">
            <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
              URL de autenticação
            </span>
            <input
              v-model="form.auth_url"
              type="text"
              autocomplete="off"
              placeholder="https://auth.banco.com/oauth2/token"
              class="mt-1 block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2 font-mono text-xs"
            >
          </label>

          <!-- Modelo de login: Token Basic (um campo so) ou usuario+senha. -->
          <div class="block">
            <span class="text-sm font-medium text-slate-700 dark:text-slate-300">Login com</span>
            <div class="mt-1 flex gap-2">
              <button
                type="button"
                class="rounded-lg border px-3 py-1.5 text-sm transition-colors"
                :class="form.login_tipo === 'basic'
                  ? 'border-woot-500 bg-woot-500 text-white'
                  : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300'"
                @click="form.login_tipo = 'basic'"
              >
                Token (Basic)
              </button>
              <button
                type="button"
                class="rounded-lg border px-3 py-1.5 text-sm transition-colors"
                :class="form.login_tipo === 'senha'
                  ? 'border-woot-500 bg-woot-500 text-white'
                  : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300'"
                @click="form.login_tipo = 'senha'"
              >
                Usuário e senha
              </button>
            </div>
          </div>

          <template v-if="form.login_tipo === 'basic'">
            <label class="block">
              <span class="text-sm font-medium text-slate-700 dark:text-slate-300">Token Basic</span>
              <span class="block text-xs text-slate-400 mb-1">
                Cole o token do banco — pode vir com o prefixo "Basic ", eu ajusto ao salvar.
              </span>
              <input
                v-model="form.api_token"
                type="password"
                autocomplete="new-password"
                :placeholder="isEdit && hasApiToken ? '•••••••• (manter)' : 'Basic MjhhMGFi…'"
                class="mt-1 block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2 font-mono text-xs"
              >
            </label>
            <label class="block">
              <span class="text-sm font-medium text-slate-700 dark:text-slate-300">Scope (opcional)</span>
              <span class="block text-xs text-slate-400 mb-1">
                Só se o banco exigir. Ex.: credit/SegueFinanceiraAPI (UY3).
              </span>
              <input
                v-model="form.scope"
                type="text"
                autocomplete="off"
                class="mt-1 block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2 font-mono text-xs"
              >
            </label>
          </template>

          <div v-else class="grid grid-cols-2 gap-3">
            <label class="block">
              <span class="text-sm font-medium text-slate-700 dark:text-slate-300">Usuário</span>
              <input
                v-model="form.username"
                type="text"
                autocomplete="off"
                class="mt-1 block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2"
              >
            </label>
            <label class="block">
              <span class="text-sm font-medium text-slate-700 dark:text-slate-300">Senha</span>
              <input
                v-model="form.password"
                type="password"
                autocomplete="new-password"
                :placeholder="isEdit && hasPassword ? '•••••••• (manter)' : ''"
                class="mt-1 block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2"
              >
            </label>
          </div>

          <!-- Credenciais extras do C6 (só aparecem ao editar o banco C6).
               Salvas no campo `extra` (jsonb) e usadas na digitação de proposta. -->
          <template v-if="form.bank_name === 'c6'">
            <label class="block">
              <span class="text-sm font-medium text-slate-700 dark:text-slate-300">Código de Origem (C6)</span>
              <span class="block text-xs text-slate-400 mb-1">codigo_origem_6 — parceiro/originador da conta C6.</span>
              <input
                v-model="form.codigo_origem_6"
                type="text"
                class="mt-1 block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2"
              >
            </label>

            <label class="block">
              <span class="text-sm font-medium text-slate-700 dark:text-slate-300">CPF do Certificado (C6)</span>
              <span class="block text-xs text-slate-400 mb-1">numero_cpf_certificado — CPF do certificado digital da conta C6.</span>
              <input
                v-model="form.numero_cpf_certificado"
                type="text"
                class="mt-1 block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2"
              >
            </label>
          </template>

          <details class="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2" :open="!!form.authText">
            <summary class="cursor-pointer text-sm font-medium text-slate-500 dark:text-slate-400 select-none">
              Avançado — JSON de autenticação (vazio = modo simples acima)
            </summary>
            <span class="block text-xs text-slate-400 mt-2 mb-1">
              A URL fica SEMPRE no campo lá em cima, não aqui. Use
              <code v-pre>{{username}}</code>, <code v-pre>{{password}}</code> e <code v-pre>{{token}}</code> pra referenciar as credenciais salvas.
            </span>
            <textarea
              v-model="form.authText"
              rows="8"
              spellcheck="false"
              :placeholder="authExample"
              class="mt-1 block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2 font-mono text-xs"
            />
          </details>

          <label class="block">
            <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
              Renovar o token a cada (minutos)
            </span>
            <input
              v-model.number="form.refresh_interval_minutes"
              type="number"
              min="0"
              placeholder="0 = manual"
              class="mt-1 block w-40 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2"
            >
            <span class="block text-xs text-slate-400 mt-1">0 = só gera manualmente (botão "Gerar agora").</span>
          </label>

          <!-- Desativado: se marcado, para de renovar o token no intervalo e o
               banco sai do uso (enabled=false). -->
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="form.desativado"
              type="checkbox"
              class="rounded border-slate-300 dark:border-slate-600"
            >
            <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
              Desativado (não gera token e não é usado)
            </span>
          </label>

          <!-- Proxy opcional por banco: Sim/Não + caixa pra colar o proxy. -->
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="form.use_proxy"
              type="checkbox"
              class="rounded border-slate-300 dark:border-slate-600"
            >
            <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
              Usar Proxy
            </span>
          </label>
          <label v-if="form.use_proxy" class="block">
            <span class="text-sm font-medium text-slate-700 dark:text-slate-300">Proxy</span>
            <span class="block text-xs text-slate-400 mb-1">Formato: http://usuario:senha@host:porta/</span>
            <input
              v-model="form.proxy_url"
              type="text"
              spellcheck="false"
              placeholder="http://usuario:senha@host:porta/"
              class="mt-1 block w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 px-3 py-2 font-mono text-xs"
            >
          </label>
        </div>

        <div class="mt-6 flex gap-2">
          <button
            type="button"
            class="flex-1 rounded-lg bg-green-600 py-2 font-medium text-white hover:bg-green-700 disabled:opacity-60"
            :disabled="saving"
            @click="save"
          >
            {{ saving ? 'Salvando…' : 'Salvar' }}
          </button>
          <button
            type="button"
            class="flex-1 rounded-lg border border-slate-300 dark:border-slate-600 py-2 hover:bg-slate-50 dark:hover:bg-slate-800"
            @click="closeModal"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
